import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  magamCertifyCheck,
  trimFormData,
  getAlertMessageFunction,
  generateImageListFromFileData,
} from '../../../common/Util';
import { parseAIData } from '../util';

import SlideRightDialog from '../../../components/common/SlideRightDialog';
import FileUploadStep from './FileUploadStep';
import LoadingStep from './LoadingStep';
import ErrorStep from './ErrorStep';
import PreviewStep from './PreviewStep';
import InputMethodStep from './InputMethodStep';
import PrivacyConsentStep from './PrivacyConsentStep';
import AiLoadingStep from './AiLoadingStep';
import PreviousEmploymentForm from './PreviousEmploymentForm';
import UnsavedChangesDialog from './UnsavedChangesDialog';
import AIResultDialog from './AIResultDialog';
import {
  uploadPreviousFile,
  loadPreviousFileResult,
  savePreviousEmployment,
  getAIParseData,
} from '../previousSlice';
import { APIRequest } from '../../../common/Common';
import { TARGET_YEAR_API } from '../../../common/data';
import { headerList } from '../headerlist';

// 단계: upload -> loading -> error/preview -> inputMethod -> privacyConsent -> aiLoading -> form
const STEPS = {
  UPLOAD: 'upload',
  LOADING: 'loading',
  ERROR: 'error',
  PREVIEW: 'preview',
  INPUT_METHOD: 'inputMethod', // AI자동반영/수동입력 선택
  PRIVACY_CONSENT: 'privacyConsent', // 개인정보 수집 동의
  AI_LOADING: 'aiLoading', // AI 자동반영 로딩
  FORM: 'form',
};

const MAX_POLLING_COUNT = 20; // 최대 폴링 횟수 (20초)

const PreviousEmploymentAdd = ({ isOpen, onClose, htmJType, htmSeq = null }) => {
  const { t } = useTranslation('previousEmployment');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useDispatch();
  const { isMagam, isCertify } = useSelector(state => state.common);

  const isEditMode = !!htmSeq; // 수정 모드 여부

  const [step, setStep] = useState(STEPS.UPLOAD);
  const [errorType, setErrorType] = useState(null); // 'serverBusy' | 'fileError'
  const [imageList, setImageList] = useState([]); // 이미지 리스트
  const [initialFormData, setInitialFormData] = useState(null); // 수정 모드용 초기 데이터
  const [isFileChanged, setIsFileChanged] = useState(false); // 파일 변경 여부 추적
  const [isFormChanged, setIsFormChanged] = useState(false); // 폼 데이터 변경 여부 추적
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false); // 저장 안됨 다이얼로그
  const [isAiParsed, setIsAiParsed] = useState(false); //ai로 반영된 데이터 확인 여부
  const [isAiEnabled, setIsAiEnabled] = useState(true); // AI 사용 가능 여부 (htmOCRAIFlag)
  const [showAiResultDialog, setShowAiResultDialog] = useState(false); // AI 결과 확인 다이얼로그
  const [aiResultData, setAiResultData] = useState(null); // AI 분석 결과 원본 데이터

  const pollingIntervalRef = useRef(null);
  const pollingCountRef = useRef(0);

  // 타입에 따른 라벨
  // 전 직장 / 납세조합
  const typeLabel = htmJType === '1' ? t('typeLabel.previousJob') : t('typeLabel.taxUnion');

  // 수정 모드일 때 기존 데이터 로드
  const loadExistingData = useCallback(async () => {
    if (!htmSeq) return;

    try {
      const response = await APIRequest(`/${TARGET_YEAR_API}/S2`, { htmSeq: htmSeq.trim() });

      if (response && response.list && response.list.length > 0) {
        const detailData = response.list[0];
        setInitialFormData(trimFormData(detailData));

        // 이미지 리스트 생성
        const fileData = response.fileList || [];
        setImageList(generateImageListFromFileData(fileData));

        // htmFlag 또는 htm024 중 하나라도 값이 있으면 바로 폼으로 이동
        if (detailData.htmFlag != null || detailData.htm024 != null) {
          setStep(STEPS.FORM);
        } else {
          // 미리보기 단계로 이동
          setStep(STEPS.PREVIEW);
        }
      }
    } catch (error) {
      console.error('기존 데이터 로드 에러:', error);
    }
  }, [htmSeq]);

  // 다이얼로그 열릴 때 수정 모드면 기존 데이터 로드
  useEffect(() => {
    if (isOpen && isEditMode) {
      loadExistingData();
    }
  }, [isOpen, isEditMode, loadExistingData]);

  // 폴링 정리
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingCountRef.current = 0;
  }, []);

  // OCR 분석 결과 폴링
  const startPolling = useCallback(() => {
    clearPolling();

    pollingIntervalRef.current = setInterval(async () => {
      pollingCountRef.current += 1;

      // 최대 폴링 횟수 초과 시 서버 과부하 에러
      if (pollingCountRef.current >= MAX_POLLING_COUNT) {
        clearPolling();
        setErrorType('serverBusy');
        setStep(STEPS.ERROR);
        return;
      }

      try {
        const result = await dispatch(loadPreviousFileResult()).unwrap();

        if (result.resultCode === '1') {
          // 성공 - 이미지 리스트 저장 후 미리보기로 이동
          clearPolling();

          // 변경된 API 응답 구조: resultData.list, resultData.htmOCRAIFlag
          const resultData = result.resultData || {};
          const fileDataRaw = resultData.list || result.fileList || [];
          const fileData = Array.isArray(fileDataRaw) ? fileDataRaw : [];
          const htmOCRAIFlag = resultData.htmOCRAIFlag;

          console.log('fileData:', fileData, 'htmOCRAIFlag:', htmOCRAIFlag);
          setImageList(generateImageListFromFileData(fileData));

          // htmOCRAIFlag: '1' = AI 사용, '2' = AI 미사용 (수기입력)
          if (htmOCRAIFlag === '2') {
            setIsAiEnabled(false);
          } else {
            setIsAiEnabled(true);
          }

          setStep(STEPS.PREVIEW);
        } else if (result.resultCode === '-1') {
          // 파일 오류 (암호화/손상 등)
          clearPolling();
          setErrorType('fileError');
          setStep(STEPS.ERROR);
        }
        // 그 외의 경우 계속 폴링
      } catch (error) {
        console.error('파일 로드 에러:', error);
        clearPolling();
        setErrorType('serverBusy');
        setStep(STEPS.ERROR);
      }
    }, 1000);
  }, [dispatch, clearPolling]);

  // 파일 선택 핸들러
  const handleFileSelect = async file => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    setStep(STEPS.LOADING);
    setIsFileChanged(true); // 파일 변경됨 표시
    setInitialFormData(null); // 기존 데이터 초기화

    try {
      const result = await dispatch(uploadPreviousFile(file)).unwrap();
      const alertMessage = getAlertMessageFunction();

      if (result.resultCode === '1') {
        // 업로드 성공 - OCR 분석 결과 폴링 시작
        startPolling();
      } else if (result.deType) {
        // 에러 메시지가 있는 경우
        alertMessage(result.deType, 'error');
        setErrorType('fileError');
        setStep(STEPS.ERROR);
      } else if (result.resultCode === '-3' && result.sessionMsg) {
        alertMessage(result.sessionMsg, 'error');
        setErrorType('fileError');
        setStep(STEPS.ERROR);
      } else {
        setErrorType('serverBusy');
        setStep(STEPS.ERROR);
      }
    } catch (error) {
      console.error('파일 업로드 에러:', error);
      setErrorType('serverBusy');
      setStep(STEPS.ERROR);
    }
  };

  /**
   * 스텝 이동 핸들러
   * @param {string} action - 액션 타입
   * - 'retry': 다시 올리기 (upload로 이동, 상태 초기화)
   * - 'skip': 건너뛰기 (form으로 이동)
   * - 'toInputMethod': 입력방식 선택으로 이동
   * - 'toFormDirect': 바로 폼으로 이동 (수정모드에서 파일 변경 없을 때)
   * - 'selectAi': AI 반영 선택 (privacyConsent로 이동)
   * - 'selectManual': 수동입력 선택 (form으로 이동)
   * - 'agreePrivacy': 개인정보 동의 (aiLoading으로 이동 후 10초 뒤 form)
   */
  const handleStepChange = action => {
    switch (action) {
      case 'retry':
        clearPolling();
        setErrorType(null);
        setIsAiParsed(false);
        setStep(STEPS.UPLOAD);
        break;
      case 'skip':
      case 'toFormDirect':
      case 'selectManual':
        clearPolling();
        setStep(STEPS.FORM);
        break;
      case 'toInputMethod':
        setStep(STEPS.INPUT_METHOD);
        break;
      case 'selectAi':
        setStep(STEPS.PRIVACY_CONSENT);
        break;
      case 'agreePrivacy':
        setStep(STEPS.AI_LOADING);
        // AI 자동반영 API 호출
        dispatch(getAIParseData())
          .unwrap()
          .then(response => {
            if (response?.resultCode === '1' && response?.resultData) {
              const headers = headerList(t, response.resultData);
              const parsedData = parseAIData(response.resultData, headers);
              setInitialFormData(parsedData);
              setIsAiParsed(true); // API 성공 시에만 isAiParsed = true
              // AI 결과 원본 데이터 저장 및 다이얼로그 표시
              setAiResultData(response.resultData);
              setShowAiResultDialog(true);
            }
          })
          .catch(error => {
            console.error('AI 파싱 데이터 로드 에러:', error);
          })
          .finally(() => {
            setStep(STEPS.FORM);
          });
        break;
      default:
        break;
    }
  };

  // 저장 핸들러
  const handleSave = async formData => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return { success: false };
    }

    try {
      const saveData = {
        ...formData,
        htmSeq: isEditMode ? htmSeq : '',
        htmJType: htmJType,
        htm002: formData.htm002?.trim() || '',
      };

      const result = await dispatch(savePreviousEmployment(saveData)).unwrap();

      if (result.resultCode === 'OK') {
        setIsFileChanged(false); // 저장 완료 시 변경 플래그 초기화
        setIsFormChanged(false);
        handleClose();
        return { success: true };
      } else {
        // 저장 실패
        return { success: false, message: tCommon('alertMessage.saveFail') };
      }
    } catch (error) {
      console.error('저장 에러:', error);
      // 저장 에러
      return { success: false, message: tCommon('alertMessage.saveFail') };
    }
  };

  // 닫기 시도 핸들러 (변경사항 확인)
  const handleCloseAttempt = () => {
    // 파일이 변경되었거나 폼 데이터가 변경된 경우 확인 다이얼로그 표시
    if (isFileChanged || isFormChanged) {
      setShowUnsavedDialog(true);
      return;
    }
    handleClose();
  };

  // 실제 닫기 핸들러
  const handleClose = () => {
    clearPolling();
    setStep(STEPS.UPLOAD);
    setErrorType(null);
    setImageList([]);
    setInitialFormData(null);
    setIsFileChanged(false);
    setIsFormChanged(false);
    setShowUnsavedDialog(false);
    setIsAiParsed(false);
    setIsAiEnabled(true);
    setShowAiResultDialog(false);
    setAiResultData(null);
    onClose();
  };

  // 저장 안됨 다이얼로그에서 나가기 선택
  const handleConfirmClose = () => {
    handleClose();
  };

  // 저장 안됨 다이얼로그에서 취소 선택
  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  // 컴포넌트 언마운트 시 폴링 정리
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  const renderStep = () => {
    switch (step) {
      // 파일 업로드
      case STEPS.UPLOAD:
        return (
          <FileUploadStep
            onFileSelect={handleFileSelect}
            typeLabel={typeLabel}
            htmJType={htmJType}
          />
        );
      // 로딩 중
      case STEPS.LOADING:
        return <LoadingStep typeLabel={typeLabel} htmJType={htmJType} />;
      // 에러 표시
      case STEPS.ERROR:
        return (
          <ErrorStep
            errorType={errorType}
            onRetry={() => handleStepChange('retry')}
            onSkip={() => handleStepChange('skip')}
          />
        );
      // 파일 미리보기
      case STEPS.PREVIEW:
        return (
          <PreviewStep
            imageList={imageList}
            onGoToForm={() =>
              isAiEnabled ? handleStepChange('toInputMethod') : handleStepChange('toFormDirect')
            }
            onGoToFormDirect={() => handleStepChange('toFormDirect')}
            onFileSelect={handleFileSelect}
            isFileChanged={isFileChanged}
            isEditMode={isEditMode}
          />
        );
      // AI자동반영/수동입력 선택
      case STEPS.INPUT_METHOD:
        return (
          <InputMethodStep
            onSelectAi={() => handleStepChange('selectAi')}
            onSelectManual={() => handleStepChange('selectManual')}
          />
        );
      // 개인정보 수집 동의
      case STEPS.PRIVACY_CONSENT:
        return <PrivacyConsentStep onAgree={() => handleStepChange('agreePrivacy')} />;
      // AI 자동반영 로딩
      case STEPS.AI_LOADING:
        return <AiLoadingStep />;
      // 데이터 입력 폼
      case STEPS.FORM:
        return (
          <PreviousEmploymentForm
            htmJType={htmJType}
            onSave={handleSave}
            onClose={handleClose}
            initialFormData={initialFormData}
            onFormChange={() => setIsFormChanged(true)}
            isAiParsed={isAiParsed}
          />
        );
      default:
        return null;
    }
  };

  // step에 따른 모달 타이틀
  const getModalTitle = () => {
    if (step === STEPS.PRIVACY_CONSENT) {
      // 개인정보 수집·이용 동의
      return t('privacyConsentSection.modalTitle');
    }
    return t('uploadSection.headerTitle');
  };

  return (
    <>
      <SlideRightDialog isOpen={isOpen} onClose={handleCloseAttempt} title={getModalTitle()}>
        {renderStep()}
      </SlideRightDialog>
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
      <AIResultDialog
        isOpen={showAiResultDialog}
        onClose={() => setShowAiResultDialog(false)}
        aiData={aiResultData}
      />
    </>
  );
};

export default PreviousEmploymentAdd;
